import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';

/**
 * Marca a requisição que já foi reexecutada, para não entrar em laço.
 *
 * `HttpContext`, e não um header: header customizado em requisição cross-origin
 * dispara preflight, e se o `Access-Control-Allow-Headers` do backend não listar
 * o nome, o browser aborta a retry ANTES de ela sair — a renovação "funciona" e
 * o segundo request nunca chega. Além disso o header vazava um detalhe interno
 * do painel para a rede. O contexto vive só no cliente e não toca o protocolo.
 */
const JA_REEXECUTADA = new HttpContextToken<boolean>(() => false);

/**
 * Renovação automática do access token (CU-86ak84mj6).
 *
 * Antes disto o painel não renovava nada: `AuthService.checkTokenExpired()` só
 * deslogava. Era por isso que o `JWT_TTL` de produção precisava ser de 8 horas —
 * qualquer valor menor virava logout no meio do trabalho. Com este interceptor,
 * o access token pode ser curto: expirou, renova e a requisição segue.
 *
 * Duas correções sobre a primeira versão:
 *
 * 1. A renovação em curso é um Observable COMPARTILHADO, e não um flag mais um
 *    BehaviorSubject. Na versão anterior o subject só recebia `next()` no
 *    caminho de sucesso: quando o refresh falhava, toda requisição que estava
 *    esperando ficava pendurada para sempre — sem `next`, sem `error`, sem
 *    `complete`. O efeito era spinner eterno e componente travado em
 *    "carregando", e o subject é `providedIn: 'root'`, então sobrevivia até ao
 *    relogin. Com `shareReplay`, quem espera recebe o mesmo desfecho, inclusive
 *    o erro.
 *
 * 2. 403 genérico não dispara mais renovação. O backend devolve 403 "Token is
 *    Expired" para token vencido, mas 403 aqui é TAMBÉM permissão negada e
 *    regra de negócio — o `DELETE /two-factor` de quem é obrigado ao segundo
 *    fator responde 403. Renovar ali queimava uma rotação de refresh à toa e,
 *    pior, REEXECUTAVA a requisição: um DELETE não idempotente saía duas vezes.
 */
@Injectable({ providedIn: 'root' })
export class RefreshInterceptor implements HttpInterceptor {

  /** A renovação em curso, compartilhada por todas as requisições que esperam. */
  private renovacao$: Observable<string> | null = null;

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const canRetry = this.isExpiredToken(error)
          && !req.context.get(JA_REEXECUTADA)
          && !this.isAuthEndpoint(req.url)
          && !!this.authService.getRefreshToken();

        if (!canRetry) {
          return throwError(() => error);
        }

        return this.refreshThenRetry(req, next);
      })
    );
  }

  /**
   * O erro é de token expirado, e não outra coisa?
   *
   * 401 é sempre credencial: token inválido ou sessão não encontrada. 403 só
   * conta quando o corpo diz, EXATAMENTE, que o token expirou.
   *
   * A comparação é exata de propósito. `includes('token')` casava qualquer
   * mensagem que mencionasse a palavra — inclusive regras de negócio que
   * respondem 403 falando de token — e cada falso positivo aqui queima uma
   * rotação de refresh e REEXECUTA a requisição. `ApiAutehnticatedMiddleware` é
   * o único que responde 403 com corpo, e usa esta string única.
   */
  private static readonly TOKEN_EXPIRADO = 'token is expired';

  private isExpiredToken(error: HttpErrorResponse): boolean {
    if (error.status === 401) {
      return true;
    }

    if (error.status !== 403) {
      return false;
    }

    const status = error.error?.status;

    return typeof status === 'string'
      && status.trim().toLowerCase() === RefreshInterceptor.TOKEN_EXPIRADO;
  }

  /** As próprias rotas de credencial não podem entrar no laço de renovação. */
  private isAuthEndpoint(url: string): boolean {
    return url.includes('/refresh-token')
      || url.includes('/login')
      || url.includes('/logout');
  }

  private refreshThenRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.tokenRenovado().pipe(
      switchMap(token => next.handle(this.withToken(req, token)))
    );
  }

  /**
   * O token novo, renovando no máximo uma vez por vez.
   *
   * `shareReplay` entrega o mesmo resultado a todos os que chegarem enquanto a
   * renovação está em curso — e `finalize` limpa a referência, para que a
   * próxima expiração comece uma renovação nova em vez de replicar a antiga.
   */
  private tokenRenovado(): Observable<string> {
    if (!this.renovacao$) {
      this.renovacao$ = this.authService.refreshToken().pipe(
        map(token => token.access_token),
        tap({
          error: () => {
            // Refresh recusado (expirado, revogado, ou família derrubada por
            // reuso): não há sessão para salvar. O AuthService já limpou os
            // dados locais.
            this.router.navigate(['/login']);
          }
        }),
        finalize(() => {
          this.renovacao$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.renovacao$;
  }

  private withToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      context: req.context.set(JA_REEXECUTADA, true)
    });
  }
}
